/**
 * Impersonation Synchronization Service
 *
 * Handles multi-tab synchronization of impersonation state using localStorage events. When impersonation starts or ends
 * in one tab, all other tabs are immediately notified and synchronize their state.
 *
 * Synchronization Mechanisms:
 *
 * - LocalStorage storage events (automatic cross-tab notification)
 * - Manual dispatch for originating tab (Safari/Chrome requirement)
 * - BroadcastChannel as fallback (if supported)
 *
 * Performance Target: <200ms synchronization latency
 *
 * @packageDocumentation
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { ImpersonationSyncEvent } from '../models/impersonation-api.model';
import { STORAGE_KEYS } from '../models/impersonation-state.model';

/**
 * Service for synchronizing impersonation state across browser tabs
 *
 * This service uses localStorage storage events to notify all tabs when impersonation starts or ends. The originating
 * tab must manually dispatch the event because storage events only fire in OTHER tabs (not the one that modified
 * localStorage).
 *
 * @example
 *   ```typescript
 *   const syncService = inject(ImpersonationSyncService);
 *
 *   // Listen for sync events
 *   syncService.syncEvents$.subscribe(event => {
 *     if (event.type === 'start') {
 *       // Another tab started impersonation
 *       this.applyImpersonationState(event);
 *     } else {
 *       // Another tab ended impersonation
 *       this.clearImpersonationState();
 *     }
 *   });
 *
 *   // Dispatch sync event (after starting impersonation)
 *   syncService.dispatchSync({ type: 'start', sessionId: '...', ... });
 *   ```;
 */
@Injectable({
  providedIn: 'root',
})
export class ImpersonationSyncService implements OnDestroy {
  /**
   * Observable stream of sync events from other tabs
   *
   * Emits when impersonation starts or ends in another tab. Subscribe to this to keep the current tab in sync.
   */
  public readonly syncEvents$ = new Subject<ImpersonationSyncEvent>();

  /** Storage event listener reference (for cleanup) */
  private readonly storageListener: (event: StorageEvent) => void;

  /** BroadcastChannel for sync (if supported) */
  private broadcastChannel: BroadcastChannel | null = null;

  constructor() {
    // Set up storage event listener
    this.storageListener = this.handleStorageEvent.bind(this);
    window.addEventListener('storage', this.storageListener);

    // Try to use BroadcastChannel as fallback (better for same-origin tabs)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.broadcastChannel = new BroadcastChannel(STORAGE_KEYS.SYNC_EVENT_KEY);
        this.broadcastChannel.onmessage = (event) => {
          this.syncEvents$.next(event.data as ImpersonationSyncEvent);
        };
      } catch (error) {
        // BroadcastChannel not supported or failed to initialize
        console.warn('BroadcastChannel not available for impersonation sync:', error);
      }
    }
  }

  /**
   * Handles storage events from localStorage
   *
   * Called automatically when localStorage is modified in ANOTHER tab. This is how cross-tab synchronization works.
   *
   * @param event - Storage event from browser
   */
  private handleStorageEvent(event: StorageEvent): void {
    // Only process events for our sync key
    if (event.key !== STORAGE_KEYS.SYNC_EVENT_KEY) {
      return;
    }

    // Ignore if no new value (shouldn't happen, but defensive)
    if (!event.newValue) {
      return;
    }

    try {
      // Performance logging: Track sync receive time
      const syncStartTime = performance.now();

      // Parse the sync event
      const syncEvent = JSON.parse(event.newValue) as ImpersonationSyncEvent;

      // Calculate sync latency
      const eventTimestamp = new Date(syncEvent.timestamp).getTime();
      const currentTimestamp = Date.now();
      const syncLatencyMs = currentTimestamp - eventTimestamp;

      // Performance logging: Log if sync exceeds 200ms target
      if (syncLatencyMs > 200) {
        console.warn('[ImpersonationSync] Multi-tab sync exceeded 200ms target', {
          latencyMs: syncLatencyMs,
          eventType: syncEvent.type,
          sessionId: syncEvent.sessionId,
        });
      } else {
        console.log('[ImpersonationSync] Multi-tab sync completed', {
          latencyMs: syncLatencyMs,
          eventType: syncEvent.type,
        });
      }

      // Emit to subscribers
      this.syncEvents$.next(syncEvent);

      const processingTimeMs = performance.now() - syncStartTime;
      if (processingTimeMs > 50) {
        console.warn('[ImpersonationSync] Slow sync event processing', { processingTimeMs });
      }
    } catch (error) {
      console.error('Failed to parse impersonation sync event:', error);
    }
  }

  /**
   * Dispatches a sync event to all tabs (including originating tab)
   *
   * This must be called by the tab that initiates impersonation start/end because storage events don't fire in the
   * originating tab.
   *
   * Synchronization flow:
   *
   * 1. Tab A starts impersonation
   * 2. Tab A calls dispatchSync({ type: 'start', ... })
   * 3. LocalStorage is updated (fires storage events in Tab B, C, D)
   * 4. Tab A emits to its own syncEvents$ (manual dispatch)
   * 5. BroadcastChannel sends to all tabs (if supported)
   * 6. All tabs receive event within ~200ms
   *
   * @example
   *   ```typescript
   *   // After starting impersonation
   *   const syncEvent: ImpersonationSyncEvent = {
   *     type: 'start',
   *     sessionId: impersonationSessionId,
   *     timestamp: new Date().toISOString(),
   *     token: impersonationToken,
   *   };
   *   this.syncService.dispatchSync(syncEvent);
   *   ```;
   *
   * @param event - Sync event to dispatch
   */
  dispatchSync(event: ImpersonationSyncEvent): void {
    try {
      // Store in localStorage (triggers storage events in other tabs)
      const eventJson = JSON.stringify(event);
      localStorage.setItem(STORAGE_KEYS.SYNC_EVENT_KEY, eventJson);

      // Manually emit to THIS tab's subscribers (storage event won't fire here)
      this.syncEvents$.next(event);

      // Also send via BroadcastChannel if available
      if (this.broadcastChannel) {
        this.broadcastChannel.postMessage(event);
      }
    } catch (error) {
      console.error('Failed to dispatch impersonation sync event:', error);
    }
  }

  /**
   * Clears sync event from localStorage
   *
   * Called after processing a sync event to clean up storage. Not strictly necessary but keeps localStorage clean.
   */
  clearSyncEvent(): void {
    try {
      localStorage.removeItem(STORAGE_KEYS.SYNC_EVENT_KEY);
    } catch (error) {
      console.error('Failed to clear sync event:', error);
    }
  }

  /** Cleanup on service destruction */
  ngOnDestroy(): void {
    // Remove storage event listener
    window.removeEventListener('storage', this.storageListener);

    // Close BroadcastChannel
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }

    // Complete the subject
    this.syncEvents$.complete();
  }
}
